'use client'
import { LinkIcon } from '@/components/icons/LinkIcon'
import { ROUTES } from '@/config/navigation'
import { Link } from '@/i18n/navigation'
import { NewsArticle } from '@/lib/news'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { Tabs } from 'radix-ui'
import { useEffect, useRef, useState } from 'react'

import { USEFUL_INFORMATION_TABS } from '../data/tabs'
import { AnnouncementsPagination } from './announcements-pagination'

const ANNOUNCEMENTS_PER_PAGE = 5
const SCROLL_OFFSET = 120

interface ICategoryTabsProps {
  selectedTab: string
  onSelectTab: (tabId: string) => void
  articles: NewsArticle[]
}

export function UsefulInformationTabs({
  selectedTab,
  onSelectTab,
  articles
}: ICategoryTabsProps) {
  const t = useTranslations('UsefulInformation')
  const tabsListRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})
  const announcementsRef = useRef<HTMLDivElement>(null)
  const [announcementsPage, setAnnouncementsPage] = useState(1)

  const handleAnnouncementsPageChange = (page: number) => {
    setAnnouncementsPage(page)
    const list = announcementsRef.current
    if (list) {
      const top =
        list.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  const scrollToTab = (tabId: string) => {
    const selectedTabElement = tabRefs.current[tabId]
    if (selectedTabElement && tabsListRef.current) {
      const tabsList = tabsListRef.current
      const tabElement = selectedTabElement

      // Calculate the position to scroll to center the tab
      const tabLeft = tabElement.offsetLeft
      const tabWidth = tabElement.offsetWidth
      const containerWidth = tabsList.offsetWidth
      const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2

      tabsList.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      })
    }
  }

  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash && USEFUL_INFORMATION_TABS.some((tab) => tab.id === hash)) {
      onSelectTab(hash)
      setTimeout(() => scrollToTab(hash), 100)
    }

    const handleHashChange = () => {
      const newHash = window.location.hash.replace('#', '')
      if (
        newHash &&
        USEFUL_INFORMATION_TABS.some((tab) => tab.id === newHash)
      ) {
        onSelectTab(newHash)
        scrollToTab(newHash)
      }
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => {
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [onSelectTab])

  const handleTabChange = (tabId: string) => {
    onSelectTab(tabId)
    window.history.replaceState(null, '', `#${tabId}`)
    scrollToTab(tabId)
  }

  const renderFinancialMediatorContent = (
    tab: (typeof USEFUL_INFORMATION_TABS)[0]
  ) => {
    return (
      <div className="space-y-4 p-2 md:p-4">
        {tab.content?.title && (
          <h4 className="text-body-2 text-primary-600 mb-4 font-bold">
            {t(tab.content.title)}
          </h4>
        )}
        {tab.image && (
          <Image src={tab.image} alt={t(tab.title)} width={72} height={94} />
        )}

        <p className="text-body-3 text-primary-600 leading-5.25">
          {tab.content.body && t(tab.content.body)}
        </p>

        <p className="text-body-3 text-primary-600 leading-5.25">
          {tab.content.address && t(tab.content.address)}
        </p>

        <p className="text-body-3 text-primary-600 leading-5.25">
          {tab.content.place && t(tab.content.place)}
        </p>

        <div className="flex flex-col">
          {tab.content.phone && (
            <span className="text-body-3 text-primary-600 leading-5.25">
              {t(tab.content.phone)}
            </span>
          )}
          {tab.content.fax && (
            <span className="text-body-3 text-primary-600 leading-5.25">
              {t(tab.content.fax)}
            </span>
          )}
          {tab.content.website && (
            <span className="text-body-3 text-primary-600 leading-5.25">
              {t(tab.content.website)}
            </span>
          )}
          {tab.content.workingHours && (
            <span className="text-body-3 text-primary-600 leading-5.25">
              {t(tab.content.workingHours)}
            </span>
          )}
          {tab.content.days && (
            <span className="text-body-3 text-primary-600 leading-5.25">
              {t(tab.content.days)}
            </span>
          )}
          {tab.content.break && (
            <span className="text-body-3 text-primary-600 leading-5.25">
              {t(tab.content.break)}
            </span>
          )}
        </div>
      </div>
    )
  }

  const renderAnnouncements = (tab: (typeof USEFUL_INFORMATION_TABS)[1]) => {
    const staticAnnouncements = tab.content.announcements ?? []
    const totalPages = Math.max(
      1,
      Math.ceil(
        (articles.length + staticAnnouncements.length) / ANNOUNCEMENTS_PER_PAGE
      )
    )
    const page = Math.min(announcementsPage, totalPages)
    const start = (page - 1) * ANNOUNCEMENTS_PER_PAGE
    const end = start + ANNOUNCEMENTS_PER_PAGE
    // Static announcements are the oldest, so they always come after the articles
    const pageArticles = articles.slice(start, end)
    const pageStaticAnnouncements = staticAnnouncements.slice(
      Math.max(0, start - articles.length),
      Math.max(0, end - articles.length)
    )

    return (
      <div ref={announcementsRef} className="space-y-4">
        {pageArticles.map((article) => (
          <Link
            href={`${ROUTES.ARTICLES}/${article.slug}`}
            className="hover:bg-primary-50/50 flex flex-col gap-4 rounded-3xl bg-gray-50 p-4 transition-colors duration-300 md:flex-row"
            key={article.slug}
          >
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl md:aspect-4/3 md:max-w-81.75">
              <Image
                src={article.thumbnail || article.image}
                alt={article.title}
                fill
                sizes="(max-width: 768px) 100vw, 327px"
                className="object-cover"
              />
            </div>
            <div className="flex w-full flex-col">
              <span className="text-caption text-primary-600 mb-4 inline-block">
                {article.date}
              </span>
              <p className="text-body-2 text-primary-600 pb-4 leading-4.75 font-bold md:leading-5.25">
                {article.title}
              </p>

              <p className="text-body-3 text-primary-400 pb-4 leading-4.75 md:leading-5.25">
                {article.preview}
              </p>

              <span className="text-primary-600 hover:text-primary-400 text-body-3 md:text-body-2 inline-block underline transition-colors">
                Մանրամասն
              </span>
            </div>
          </Link>
        ))}
        {pageStaticAnnouncements.map((announcement) => (
          <Link
            href={announcement.body.link}
            className="hover:bg-primary-50/50 flex flex-col gap-4 rounded-3xl bg-gray-50 p-4 transition-colors duration-300 md:flex-row"
            key={t(announcement.title)}
          >
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl md:aspect-4/3 md:max-w-81.75">
              <Image
                src={announcement.image}
                alt={t(announcement.title)}
                fill
                sizes="(max-width: 768px) 100vw, 327px"
                className="object-cover"
              />
            </div>
            <div className="flex w-full flex-col">
              <span className="text-caption text-primary-600 mb-4 inline-block">
                {t(announcement.date)}
              </span>
              <p className="text-body-2 text-primary-600 pb-4 leading-4.75 font-bold md:leading-5.25">
                {t(announcement.title)}
              </p>

              <p className="text-body-3 text-primary-400 leading-4.75 md:leading-5.25">
                {t(announcement.body.p1)}
              </p>

              <p className="text-body-3 text-primary-400 pb-4 leading-4.75 md:leading-5.25">
                {t(announcement.body.p2)}
              </p>

              <span className="text-primary-600 hover:text-primary-400 text-body-3 md:text-body-2 inline-block underline transition-colors">
                {t(announcement.body.linkText)}
              </span>
            </div>
          </Link>
        ))}
        <AnnouncementsPagination
          page={page}
          totalPages={totalPages}
          onPageChange={handleAnnouncementsPageChange}
        />
      </div>
    )
  }

  const renderUsefulLinks = (tab: (typeof USEFUL_INFORMATION_TABS)[2]) => {
    return (
      <div className="flex flex-col gap-4">
        {tab.content.links?.map((link) => (
          <div key={link.id} className="rounded-2xl bg-gray-50 p-2 md:p-4">
            <div className="mb-3 flex justify-between">
              <p className="text-primary-600 text-body-2 font-bold">
                {t(link.title)}
              </p>
              <LinkIcon className="text-primary-600 ml-auto h-5 w-5" />
            </div>
            <Link
              href={link.url}
              target="_blank"
              className="text-primary-600 text-body-3 underline"
            >
              {link.url}
            </Link>
          </div>
        ))}
      </div>
    )
  }

  return (
    <Tabs.Root
      className="flex w-full flex-col"
      defaultValue="legalActs"
      value={selectedTab}
      onValueChange={handleTabChange}
    >
      <Tabs.List
        ref={tabsListRef}
        className="no-scrollbar mx-auto flex w-full max-w-182.5 shrink-0 gap-4 overflow-x-scroll md:mb-6 md:justify-center md:gap-8"
        aria-label={t('title')}
      >
        {USEFUL_INFORMATION_TABS.map((tab) => (
          <Tabs.Trigger
            key={tab.id}
            ref={(el) => {
              tabRefs.current[tab.id] = el
            }}
            className="text-primary-400 hover:text-primary-500 data-[state=active]:text-primary-500 data-[state=active]:border-primary-500 text-body-3 h-11.25 w-fit cursor-pointer items-center justify-center px-4 whitespace-nowrap ring-offset-1 transition-all ease-in data-[state=active]:border-b-2"
            value={tab.id}
          >
            {t(tab.title)}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {USEFUL_INFORMATION_TABS.map((tab) => (
        <Tabs.Content
          key={tab.id}
          value={tab.id}
          className="mx-auto my-4 w-full space-y-4 rounded-3xl bg-white p-6 shadow-2xl"
        >
          {tab.id === 'financial-mediator' &&
            renderFinancialMediatorContent(tab)}

          {tab.id === 'announcements' && renderAnnouncements(tab)}
          {tab.id === 'links' && renderUsefulLinks(tab)}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  )
}
